<template>
  <div>
    <template
      v-if="
        props.id !== '0' || store.state.global.context.selection.organisation
      "
    >
      <BaseForm
        ref="baseForm"
        :data-object="roleTransform"
        :pre-update-hook="updateRole"
        :content-type="'role'"
        :column-width="'is-12'"
      >
        <template #content>
          <v-container>
            <v-row>
              <v-text-field
                v-model.trim="role.name"
                label="Name"
                placeholder="Name"
                icon="text-short"
              />

              <v-text-field
                v-model.trim="role.description"
                label="Description"
                placeholder="Description"
                icon="text-short"
              />
            </v-row>
          </v-container>

          <h3 class="subtitle">Permissions</h3>
          <v-switch v-model="propagate" label="Propagate edits to children" />
          <DependencyTreeItem
            v-if="!loading"
            :id="props.id"
            :role="role"
            :elements="depTreeItems"
            :propagate="propagate"
          />

          <hr />
          <v-sheet>
            <v-btn
              :prepend-icon="
                showUnhandled ? 'mid-eye-off-outline' : 'mdi-eye-outline'
              "
              color="success"
              @click="showUnhandled = !showUnhandled"
            >
              {{ !showUnhandled ? 'Show unhandled' : 'Hide unhandled' }}
            </v-btn>
          </v-sheet>
          <div v-if="showUnhandled" class="columns is-multiline">
            <div
              v-for="(perms, objectType) in unhandledPermissions"
              :key="objectType"
              class="box column is-4"
            >
              <h2 class="subtitle">{{ objectType }}</h2>
              <div
                v-for="(value, permission) in perms"
                :key="permission"
                class="field"
              >
                <v-switch
                  v-model="additionalPermissions[objectType][permission]"
                  color="success"
                  :label="String(permission)"
                />
              </div>
            </div>
          </div>
          <hr />
          <h3>Export / Import Role</h3>
          <v-sheet>
            <v-btn class="button" @click="exportRole(true)">
              Export (only true permissions)
            </v-btn>
            <v-btn class="button" @click="exportRole(false)">
              Export (all)
            </v-btn>
          </v-sheet>
          <v-text-field
            v-model="importText"
            label="Import JSON"
            type="textarea"
            class="code-editor"
            @update:model-value="validateJSON"
          />

          <v-btn class="button" @click="importRole">Import</v-btn>

          <v-alert v-if="errorMessage" type="error">{{ errorMessage }}</v-alert>
        </template>
      </BaseForm>
    </template>
    <v-alert v-else title="Warning" type="warning" />
    <LoadingOverlay v-model:loading="loading" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useSnackbar } from 'vuetify-use-dialog'
import { useStore } from 'vuex'

import { apiClient } from '@/api/ApiClient'
import errorHandler from '@/api/ErrorHandler'
import BaseForm from '@/components/common/BaseForm.vue'
import LoadingOverlay from '@/components/common/LoadingOverlay.vue'
import { ROLE_DEFAULT } from '@/models/core/defaults'
import {
  PERMISSIONS_DEFAULT,
  ROLE_DEPENDENCY_TREE,
} from '@/models/core/defaults'
import { Role } from '@/models/core/models'
import { useCopy } from '@/util/clipboard'
const { copyTextToClipboard } = useCopy()

import DependencyTree from './DependencyTree.vue'
const createSnackbar = useSnackbar()
const store = useStore()
const props = defineProps({
  id: {
    required: true,
    type: String,
  },
})
const role = ref(JSON.parse(JSON.stringify(ROLE_DEFAULT)))
const depTreeItems = ref(JSON.parse(JSON.stringify(ROLE_DEPENDENCY_TREE)))
const showUnhandled = ref(false)
const additionalPermissions = ref({})
const baseForm = ref(null)
const loading = ref(false)
const importText = ref('')
const errorMessage = ref('')
const propagate = ref(true)

onMounted(() => {
  loading.value = true
  apiClient.get('role', props.id).then((response) => {
    role.value = orderAndFilterPermissions(response)
    loading.value = false
  })
})

const roleTransform = computed(() => {
  if (props.id === '0') {
    role.value.organisation = store.getters['global/organisation'].id
  }

  // apply the additional permissions we dont know about back
  for (const objType in additionalPermissions.value) {
    for (const permissionName in additionalPermissions.value[objType]) {
      role.value.permissions[objType][permissionName] =
        additionalPermissions.value[objType][permissionName]
    }
  }
  return role.value
})

function orderAndFilterPermissions(role) {
  const formattedPermissions = {}
  let out: Role
  for (const objType in role.permissions) {
    if (role.permissions.hasOwnProperty(objType)) {
      formattedPermissions[objType] = {}
      for (const permissionName in PERMISSIONS_DEFAULT[objType]) {
        if (role.permissions[objType].hasOwnProperty(permissionName)) {
          formattedPermissions[objType][permissionName] =
            role.permissions[objType][permissionName]
        }
      }

      // For permissions that are not registered with the frontend yet, we just add them to the end
      for (const permissionName in role.permissions[objType]) {
        if (additionalPermissions.value[objType] === undefined) {
          additionalPermissions[objType] = {}
        }
        if (PERMISSIONS_DEFAULT[objType] === undefined) {
          additionalPermissions[objType][permissionName] =
            role.permissions[objType][permissionName]
        } else if (
          !PERMISSIONS_DEFAULT[objType].hasOwnProperty(permissionName)
        ) {
          additionalPermissions[objType][permissionName] =
            role.permissions[objType][permissionName]
        }
      }
    }
  }
  out = role
  out.permissions = formattedPermissions
  return out
}

const unhandledPermissions = computed(() => {
  let out = {}
  Object.keys(additionalPermissions.value).forEach((objectType) => {
    Object.keys(additionalPermissions.value[objectType]).forEach((perm) => {
      if (perm !== 'owner') {
        if (out[objectType] === undefined) {
          out[objectType] = {}
        }
        out[objectType][perm] = additionalPermissions.value[objectType][perm]
      }
    })
  })
  return out
})

function updateRole() {}

function exportRole(onlyTrue = false) {
  const roleCopy: Role = JSON.parse(JSON.stringify(role.value))
  if (onlyTrue) {
    for (let obj in roleCopy.permissions) {
      for (let permission in roleCopy.permissions[obj]) {
        if (!roleCopy.permissions[obj][permission]) {
          // only keep true permissions
          delete roleCopy.permissions[obj][permission]
        }
      }
    }
    for (let obj in roleCopy.permissions) {
      if (Object.keys(roleCopy.permissions[obj]).length === 0) {
        // only keep objects that have any true permission
        delete roleCopy.permissions[obj]
      }
    }
  }
  // empty unnecessary fields
  roleCopy.id = ''
  roleCopy.organisation = ''
  roleCopy.create_time = ''
  roleCopy.creator = ''
  roleCopy._permissions = {}
  // sort
  const orderedPermissions = {}
  Object.keys(roleCopy.permissions)
    .sort()
    .forEach((key) => {
      orderedPermissions[key] = roleCopy.permissions[key]
    })
  roleCopy.permissions = orderedPermissions
  for (let obj in roleCopy.permissions) {
    const ordered = {}
    Object.keys(roleCopy.permissions[obj])
      .sort()
      .forEach((key) => {
        ordered[key] = roleCopy.permissions[obj][key]
      })
    roleCopy.permissions[obj] = ordered
  }
  // copy to clipboard
  copyTextToClipboard(
    JSON.stringify(roleCopy, null, 2),
    'Copied role to clipboard'
  )
}

function validateJSON() {
  try {
    const role: Role = JSON.parse(importText.value)
    errorMessage.value = ''
  } catch (err) {
    errorMessage.value = errorHandler.errorToString(err)
  }
}

function importRole() {
  // do not propagate
  propagate.value = false
  try {
    const newRole: Role = JSON.parse(importText.value)
    for (let obj in role.value.permissions) {
      for (let permission in role.value.permissions[obj]) {
        if (
          role.value.permissions[obj] &&
          role.value.permissions[obj][permission] !== undefined
        ) {
          role.value.permissions[obj][permission] =
            role.value.permissions[obj][permission]
        }
      }
    }
    role.value = orderAndFilterPermissions(newRole)
    createSnackbar({
      text: 'Role imported',
      snackbarProps: { color: 'green-darken-3', timeout: 5000 },
    })
  } catch (err) {
    errorMessage.value = errorHandler.errorToString(err)
  }
}
</script>
<style lang="scss">
.select select[multiple] {
  min-height: 100px !important;
}

.field .code-editor .textarea {
  font-family: monospace;
  background: rgb(245, 245, 245);
  height: 200px;
}
</style>

// BaseForm content for this component
<!--  -->
